import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { isFulfilled } from "@reduxjs/toolkit";
import AddIcon from "@mui/icons-material/Add";
import { format } from "date-fns";
import { DesktopDatePicker } from "@mui/x-date-pickers-pro";
import { useAppDispatch } from "../../../../../../../app/hooks";
import { CustomButton } from "../../../../../../../common/components/functional/customButton/CustomButton";
import BasicDialog from "../../../../../../../common/components/functional/dialogs/BasicDialog";
import OverlaySpinner from "../../../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { isoFormat } from "../../../../../../../common/helpers/formatDateTime";
import useBackendPolling from "../../../../../../../hooks/useBackendPolling";
import {
  ApprovalReadinessOptions,
  WorkAgreementDocumentInfo,
  WorkAgreementDocumentTypeOptions,
} from "../../../../../orderAPI";
import {
  createWorkAgreementDocument,
  getWorkAgreementDocumentInfo,
  getWorkAgreementDocumentUploadUrl,
  setWorkAgreementDocumentSigningDate,
  uploadWorkAgreementDocument,
} from "../../../../../orderRowsDetailedSlice";
import { FormKeys, WORK_AGREEMENT_DOCUMENT_FILE_TYPES } from "../../helpers";

export type WorkAgreementDocumentDialogResult = {
  workAgreementDocument: File;
  additionalDocuments: File[];
  originalSigningDate: Date;
};

type Props = {
  open: boolean;
  talentId: string;
  initialWorkAgreementDocument: File | null;
  initialAdditionalDocuments: File[];
  initialSigningDate: Date | null;
  onCancel: () => void;
  onSaved: (result: WorkAgreementDocumentDialogResult) => void;
  onSavingChange: (isSaving: boolean) => void;
};

export const WorkAgreementDocumentDialog = ({
  open,
  talentId,
  initialWorkAgreementDocument,
  initialAdditionalDocuments,
  initialSigningDate,
  onCancel,
  onSaved,
  onSavingChange,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();

  const [workAgreementDocument, setWorkAgreementDocument] =
    useState<File | null>(null);
  const [additionalDocuments, setAdditionalDocuments] = useState<File[]>([]);
  const [originalSigningDate, setOriginalSigningDate] = useState<Date | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const wasPollingRef = useRef(false);
  const expectedAttachmentNamesRef = useRef<string[]>([]);
  const hasRequestedSigningDateRef = useRef(false);
  const originalSigningDateRef = useRef<Date | null>(null);

  const setSaving = (nextIsSaving: boolean) => {
    setIsSaving(nextIsSaving);
    onSavingChange(nextIsSaving);
  };

  useEffect(() => {
    if (!open) return;
    setWorkAgreementDocument(initialWorkAgreementDocument);
    setAdditionalDocuments(initialAdditionalDocuments);
    setOriginalSigningDate(initialSigningDate);
    // Reset dialog fields only when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCancel = () => {
    if (isSaving) return;
    onCancel();
  };

  const shouldContinuePolling = async () => {
    const workAgreementDocumentId = getValues(FormKeys.WorkAgreementDocumentId);
    if (!workAgreementDocumentId) return;
    const response: WorkAgreementDocumentInfo = await dispatch(
      getWorkAgreementDocumentInfo({ workAgreementDocumentId }),
    ).unwrap();
    const attachments = response.attachments ?? [];
    const signingDate = originalSigningDateRef.current;

    if (
      response.approvalReadiness ===
        ApprovalReadinessOptions.SIGNING_DATE_MISSING &&
      signingDate &&
      !hasRequestedSigningDateRef.current
    ) {
      hasRequestedSigningDateRef.current = true;
      const action = await dispatch(
        setWorkAgreementDocumentSigningDate({
          workAgreementId: workAgreementDocumentId,
          signingDate: format(signingDate, isoFormat),
          version: response.version,
        }),
      );
      if (!isFulfilled(action)) {
        hasRequestedSigningDateRef.current = false;
      }
      return;
    }

    const expectedAttachmentNames = expectedAttachmentNamesRef.current;
    const areAdditionalFilesUploaded =
      expectedAttachmentNames.length === additionalDocuments.length &&
      expectedAttachmentNames.every((filename) =>
        attachments.includes(filename),
      );

    if (
      response.approvalReadiness === ApprovalReadinessOptions.READY &&
      areAdditionalFilesUploaded
    ) {
      stopPolling();
    }
  };

  const { isPolling, startPolling, stopPolling, isTimeout, error } =
    useBackendPolling({
      callback: async () => shouldContinuePolling(),
      delay: 5000,
      maxAttempts: 30,
      errorMessage: t("order:workAgreementDocumentPollingError"),
    });

  useEffect(() => {
    if (isPolling) {
      wasPollingRef.current = true;
      return;
    }
    if (!wasPollingRef.current || !isSaving) return;

    wasPollingRef.current = false;
    setSaving(false);
    if (!error && !isTimeout && workAgreementDocument && originalSigningDate) {
      onSaved({
        workAgreementDocument,
        additionalDocuments,
        originalSigningDate,
      });
    } else if (error || isTimeout) {
      setValue(FormKeys.WorkAgreementDocumentId, null);
    }
    // Files are read when polling stops; they do not change during polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, isSaving, error, isTimeout]);

  const handleConfirm = () => {
    if (!workAgreementDocument || !originalSigningDate || isSaving) return;
    setSaving(true);
    expectedAttachmentNamesRef.current = [];
    hasRequestedSigningDateRef.current = false;
    originalSigningDateRef.current = originalSigningDate;
    const payload = {
      talentId,
      bundleId: getValues("bundleId"),
    };
    dispatch(createWorkAgreementDocument(payload)).then((action) => {
      if (!isFulfilled(action)) {
        setSaving(false);
        return;
      }
      const workAgreementDocumentId = action.payload[0].id;
      setValue(FormKeys.WorkAgreementDocumentId, workAgreementDocumentId);
      dispatch(
        getWorkAgreementDocumentUploadUrl({
          workAgreementDocumentId,
          type: WorkAgreementDocumentTypeOptions.MAIN,
          size: workAgreementDocument.size,
          filename: workAgreementDocument.name,
        }),
      ).then((action) => {
        if (!isFulfilled(action)) {
          setSaving(false);
          setValue(FormKeys.WorkAgreementDocumentId, null);
          return;
        }
        const url = action.payload.url;
        startPolling();
        dispatch(
          uploadWorkAgreementDocument({ url, file: workAgreementDocument }),
        );
      });
      additionalDocuments.forEach((document) => {
        dispatch(
          getWorkAgreementDocumentUploadUrl({
            workAgreementDocumentId,
            type: WorkAgreementDocumentTypeOptions.ATTACHMENT,
            size: document.size,
            filename: document.name,
          }),
        ).then((action) => {
          if (isFulfilled(action)) {
            expectedAttachmentNamesRef.current.push(action.payload.filename);
            const url = action.payload.url;
            dispatch(uploadWorkAgreementDocument({ url, file: document }));
          }
        });
      });
    });
  };

  const dialogContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <CustomButton
          variant="contained"
          component="label"
          startIcon={<AddIcon />}
          sx={{ borderRadius: "24px", maxWidth: "372px" }}
        >
          <input
            type="file"
            accept={WORK_AGREEMENT_DOCUMENT_FILE_TYPES.toString()}
            hidden
            onChange={(event) => {
              const files = event.target.files;
              if (files) {
                setWorkAgreementDocument(files[0]);
              }
            }}
          />
          {t("order:uploadReplacementWorkAgreement")}
        </CustomButton>
        {workAgreementDocument && (
          <Typography>{`${t("sendEmail:attachment")}: ${workAgreementDocument.name}`}</Typography>
        )}
        <Typography>{t("order:uploadReplacementWorkAgreementInfo")}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <CustomButton
          variant="outlined"
          startIcon={<AddIcon />}
          component="label"
          sx={{ borderRadius: "24px", maxWidth: "372px" }}
        >
          <input
            type="file"
            accept={WORK_AGREEMENT_DOCUMENT_FILE_TYPES.toString()}
            hidden
            onChange={(event) => {
              const files = event.target.files;
              if (files) {
                setAdditionalDocuments([...additionalDocuments, files[0]]);
              }
            }}
          />
          {t("order:uploadAdditionalDocument")}
        </CustomButton>
        {additionalDocuments.length > 0 && (
          <Box>
            <Typography>
              {`${t("common:attachments")}: ${additionalDocuments.map((document) => document.name).join(", ")}`}
            </Typography>
          </Box>
        )}
        <Typography>{t("order:uploadAdditionalDocumentInfo")}</Typography>
      </Box>
      <Typography>{t("order:workAgreementOfferDataRequired")}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography>
          {t("order:enterWorkAgreementOriginalSigningDate")}
        </Typography>
        <DesktopDatePicker
          value={originalSigningDate}
          onChange={(value: Date | null) => setOriginalSigningDate(value)}
          sx={{ maxWidth: "372px" }}
          slotProps={{
            textField: {
              size: "small",
            },
          }}
          maxDate={new Date()}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <OverlaySpinner isLoading={isSaving} sx={{ zIndex: 10000 }} />
      <BasicDialog
        open={open}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        title={t("order:workAgreementDocument")}
        content={dialogContent}
        confirmDisabled={
          !workAgreementDocument || !originalSigningDate || isSaving
        }
      />
    </>
  );
};
